import React from "react";
import { styled } from "@mui/material/styles";
import { Search } from "react-feather";
import { tokens as t } from "./tokens";

/* ---------- Search ---------- */
const SearchWrap = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 9,
  background: t.color.canvas,
  border: "1px solid transparent",
  borderRadius: t.radius.md,
  padding: "0 12px",
  height: 40,
  minWidth: 240,
  transition: "all .15s ease",
  "&:focus-within": {
    background: "#fff",
    borderColor: t.color.brand,
    boxShadow: `0 0 0 4px ${t.color.brandSoft}`,
  },
  "& svg": { width: 17, height: 17, color: t.color.muted2, flex: "none" },
  "& input": {
    border: "none",
    outline: "none",
    background: "none",
    flex: 1,
    fontFamily: t.font.body,
    fontSize: 13,
    color: t.color.text,
  },
});

export const SearchInput = ({ value, onChange, placeholder = "Хайх…", ...rest }) => (
  <SearchWrap>
    <Search />
    <input
      value={value}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      {...rest}
    />
  </SearchWrap>
);

/* ---------- bare select (toolbar) ---------- */
export const SelectField = styled("select")({
  height: 40,
  border: `1px solid ${t.color.line}`,
  borderRadius: t.radius.md,
  padding: "0 12px",
  background: "#fff",
  fontFamily: t.font.body,
  fontSize: 13,
  fontWeight: 500,
  color: t.color.text,
  cursor: "pointer",
  minWidth: 150,
  "&:focus": {
    outline: "none",
    borderColor: t.color.brand,
    boxShadow: `0 0 0 4px ${t.color.brandSoft}`,
  },
});

/* ---------- Form field shells ---------- */
const FieldWrap = styled("div")({ marginBottom: 15 });
const FieldLabel = styled("label")({
  display: "block",
  fontFamily: t.font.body,
  fontSize: 12,
  fontWeight: 600,
  color: "#334155",
  marginBottom: 7,
});
const ErrorText = styled("div")({
  fontFamily: t.font.body,
  fontSize: 11,
  fontWeight: 600,
  color: t.color.red,
  marginTop: 6,
});

const baseInput = (error) => ({
  width: "100%",
  border: `1.5px solid ${error ? t.color.red : t.color.line}`,
  borderRadius: t.radius.md,
  padding: "0 13px",
  fontFamily: t.font.body,
  fontSize: 13.5,
  color: t.color.text,
  outline: "none",
  transition: "all .15s ease",
  background: "#fff",
  "&:focus": {
    borderColor: error ? t.color.red : t.color.brand,
    boxShadow: `0 0 0 4px ${error ? t.color.redSoft : t.color.brandSoft}`,
  },
});

const Input = styled("input", { shouldForwardProp: (p) => p !== "hasError" })(({ hasError }) => ({
  ...baseInput(hasError),
  height: 44,
}));
const Area = styled("textarea", { shouldForwardProp: (p) => p !== "hasError" })(({ hasError }) => ({
  ...baseInput(hasError),
  minHeight: 82,
  padding: "11px 13px",
  resize: "vertical",
}));
const Sel = styled("select", { shouldForwardProp: (p) => p !== "hasError" })(({ hasError }) => ({
  ...baseInput(hasError),
  height: 44,
  cursor: "pointer",
  appearance: "none",
  backgroundImage:
    "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  paddingRight: 36,
}));

/* Text / textarea field */
export const Field = React.forwardRef(({ label, multiline, error, errorText, ...rest }, ref) => (
  <FieldWrap>
    {label && <FieldLabel>{label}</FieldLabel>}
    {multiline ? (
      <Area ref={ref} hasError={!!error} {...rest} />
    ) : (
      <Input ref={ref} hasError={!!error} {...rest} />
    )}
    {error && errorText && <ErrorText>{errorText}</ErrorText>}
  </FieldWrap>
));
Field.displayName = "Field";

/* Select field with label.  items: [{ name, value }] */
export const SelectBox = ({ label, value, onChange, items = [], placeholder = "Сонгох…", error, errorText }) => (
  <FieldWrap>
    {label && <FieldLabel>{label}</FieldLabel>}
    <Sel hasError={!!error} value={value ?? ""} onChange={(e) => onChange?.(e.target.value)}>
      <option value="" disabled>
        {placeholder}
      </option>
      {items.map((it, i) => (
        <option key={it.value ?? i} value={it.value}>
          {it.name}
        </option>
      ))}
    </Sel>
    {error && errorText && <ErrorText>{errorText}</ErrorText>}
  </FieldWrap>
);

export const Grid2 = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 14,
});

export default SearchInput;
